import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../Icons';
import { TooltipTrigger, TooltipContent, TooltipProvider, Tooltip } from '../Tooltip';
import { Separator } from '../Separator';
import { TbUnlink } from 'react-icons/tb';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as CryptoJS from 'crypto-js';
import {
  AUTH_TOKEN_API,
  BLOCKED_KEYWORDS,
  FULL_SCREEN_REPORT,
  GET_STAFF_INFO,
  MASTER_API,
  PACS_API,
} from '../../../../../environment';

type StyleMap = {
  open: {
    left: { marginLeft: string };
    right: { marginRight: string };
  };
  closed: {
    left: { marginLeft: string; right?: string };
    right: { marginRight: string; left?: string };
  };
};
const borderSize = 4;
const collapsedWidth = 25; // Reference value, not used for width
const closeIconWidth = 30;
const gridHorizontalPadding = 0;
const tabSpacerWidth = 0;
let editorWindow: Window | null = null;

const baseClasses =
  'transition-all duration-300 ease-in-out bg-grey-main border-black justify-start box-content flex flex-col ';
const classesMap = {
  open: {
    left: ``,
    right: ``,
  },
  closed: {
    left: `items-end`,
    right: `items-start`,
  },
};

const openStateIconName = {
  left: 'SidePanelCloseLeft',
  right: 'SidePanelCloseRight',
};

const getTabWidth = (numTabs: number) => {
  if (numTabs < 3) {
    return 68;
  } else {
    return 40;
  }
};

const getGridWidth = (numTabs: number, gridAvailableWidth: number) => {
  const spacersWidth = (numTabs - 1) * tabSpacerWidth;
  const tabsWidth = getTabWidth(numTabs) * numTabs;

  if (gridAvailableWidth > tabsWidth + spacersWidth) {
    return tabsWidth + spacersWidth;
  }

  return gridAvailableWidth;
};

const getNumGridColumns = (numTabs: number, gridWidth: number) => {
  if (numTabs === 1) {
    return 1;
  }

  const tabWidth = getTabWidth(numTabs);
  const numTabsWithOneSpacerEach = Math.floor(gridWidth / (tabWidth + tabSpacerWidth));

  if (
    (numTabsWithOneSpacerEach + 1) * tabWidth + numTabsWithOneSpacerEach * tabSpacerWidth <=
    gridWidth
  ) {
    return numTabsWithOneSpacerEach + 1;
  }

  return numTabsWithOneSpacerEach;
};

const getTabClassNames = (
  numColumns: number,
  numTabs: number,
  tabIndex: number,
  isActiveTab: boolean,
  isTabDisabled: boolean
) =>
  classnames('h-[28px] mb-[2px] cursor-pointer text-white bg-black', {
    'hover:text-lightblue-main': !isActiveTab && !isTabDisabled,
    'rounded-l': tabIndex % numColumns === 0,
    'rounded-r': (tabIndex + 1) % numColumns === 0 || tabIndex === numTabs - 1,
  });

const getTabStyle = (numTabs: number) => {
  return {
    width: `${getTabWidth(numTabs)}px`,
  };
};

const getTabIconClassNames = (numTabs: number, isActiveTab: boolean) => {
  return classnames('h-full w-full flex items-center justify-center', {
    'bg-primary/30': isActiveTab,
  });
};

const SidePanel = ({
  side,
  className,
  activeTabIndex,
  tabs,
  onActiveTabIndexChange,
  expandedWidth = 450,
  onClose,
  isOpen,
}) => {
  const gridAvailableWidth = expandedWidth - closeIconWidth - gridHorizontalPadding;
  const gridWidth = getGridWidth(tabs.length, gridAvailableWidth);
  const openStatus = isOpen ? 'open' : 'closed';

  const updateActiveTabIndex = useCallback(
    (newActiveTabIndex: number) => {
      if (onActiveTabIndexChange) {
        onActiveTabIndexChange({ activeTabIndex: newActiveTabIndex });
      }
    },
    [onActiveTabIndexChange]
  );

  const detachedReportWindow = useCallback(() => {
    let queryParams = new URLSearchParams(window.location.search);

    // Check for encrypted params
    const encryptedParams = queryParams.get('params');
    if (encryptedParams) {
      try {
        const decrypted = CryptoJS.AES.decrypt(encryptedParams, 'z!!!!!!!1sdfadsf56adf456asdfasdf');
        const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
        const decryptedParams = new URLSearchParams();
        if (decryptedString.trim().startsWith('{')) {
          const decryptedJson = JSON.parse(decryptedString);
          Object.entries(decryptedJson).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              decryptedParams.set(key, String(value));
            }
          });
        } else {
          const parsed = new URLSearchParams(decryptedString);
          for (const [key, value] of parsed) {
            decryptedParams.set(key, value);
          }
        }
        // Merge decrypted params into queryParams
        for (const [key, value] of decryptedParams) {
          const existingValue = queryParams.get(key);
          if (
            existingValue === null ||
            existingValue === '' ||
            existingValue === 'null' ||
            existingValue === 'undefined'
          ) {
            queryParams.set(key, value);
          }
        }
      } catch (error) {
        console.error('Failed to decrypt params:', error);
      }
    }

    const studyInstanceUID = queryParams.get('StudyInstanceUIDs');
    const modality = queryParams.get('modality');
    const unit = queryParams.get('unit');
    const staffId = queryParams.get('staffId');
    const role = queryParams.get('role');
    const userName = queryParams.get('userName');

    const editorUrl = `${FULL_SCREEN_REPORT}patient_report.html?studyInstanceUID=${studyInstanceUID}&unit=${unit}&modality=${modality}&staffID=${staffId}&role=${role}&userName=${userName}&PACS_API=${PACS_API}&AUTH_TOKEN_API=${AUTH_TOKEN_API}&GET_STAFF_INFO=${GET_STAFF_INFO}&MASTER_API=${MASTER_API}&BLOCKED_KEYWORDS=${BLOCKED_KEYWORDS}`;

    if (editorWindow && !editorWindow.closed) {
      editorWindow.focus();
      return;
    }

    editorWindow = window.open(
      editorUrl,
      `${studyInstanceUID || 'report'}`,
      'width=1000,height=800,scrollbars=yes,resizable=yes'
    );
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const getToolTipContent = (label: string, disabled: boolean) => {
    return (
      <>
        <div>{label}</div>
        {disabled && <div className="text-white">{'Not available based on current context'}</div>}
      </>
    );
  };

  const renderIcon = (iconName: string, props: any) => {
    const IconComponent = Icons[iconName];
    if (!IconComponent) {
      return <div style={{ width: '22px', height: '22px' }} />;
    }

    return React.createElement(IconComponent, props);
  };

  const getCloseStateComponent = () => {
    const _childComponents = Array.isArray(tabs) ? tabs : [tabs];
    return (
      <>
        <div
          className={classnames(
            'bg-grey-dark flex h-[28px] w-full cursor-pointer items-center rounded-md',
            side === 'left' ? 'justify-end pr-2' : 'justify-start pl-2'
          )}
          onClick={() => {
            updateActiveTabIndex(activeTabIndex);
          }}
          data-cy={`side-panel-header-${side}`}
        >
          <Icons.NavigationPanelReveal
            className={classnames('text-lightblue-main', side === 'left' && 'rotate-180 transform')}
          />
        </div>
        <div className={classnames('mt-3 flex flex-col space-y-3')}>
          <TooltipProvider>
            {_childComponents.map((childComponent, index) => (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <div
                    id={`${childComponent.name}-btn`}
                    data-cy={`${childComponent.name}-btn`}
                    className="text-lightblue-main hover:cursor-pointer"
                    onClick={() => {
                      return childComponent.disabled ? null : updateActiveTabIndex(index);
                    }}
                  >
                    {renderIcon(childComponent.iconName, {
                      className: classnames({
                        'text-lightblue-main': true,
                        'ohif-disabled': childComponent.disabled,
                      }),
                      style: {
                        width: '22px',
                        height: '22px',
                      },
                    })}
                  </div>
                </TooltipTrigger>
                <TooltipContent side={side === 'left' ? 'right' : 'left'}>
                  <div
                    className={classnames(
                      'flex items-center',
                      side === 'left' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {getToolTipContent(childComponent.label, childComponent.disabled)}
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </div>
      </>
    );
  };

  const getCloseIcon = () => {
    return (
      <div
        className={classnames(
          'relative flex cursor-pointer items-center justify-center text-gray-300 hover:text-white',
          side === 'right' ? 'right-0' : 'left-0' // Adjusted for right panel inner edge
        )}
        style={{ width: `${closeIconWidth}px` }}
        onClick={onClose}
        data-cy={`side-panel-header-${side}`}
      >
        {renderIcon(openStateIconName[side], {})}
      </div>
    );
  };

  const getTabGridComponent = () => {
    const numColumns = getNumGridColumns(tabs.length, gridWidth);
    const activeTabName = tabs?.[activeTabIndex]?.name;
    const showDetach = activeTabName === 'Report';

    return (
      <>
        {getCloseIcon()}
        <div className={classnames('flex grow justify-center')}>
          <div className={classnames('bg-primary-dark text-lightblue-main flex flex-wrap')}>
            {tabs.map((tab, tabIndex) => {
              const { disabled } = tab;
              return (
                <React.Fragment key={tabIndex}>
                  {tabIndex % numColumns !== 0 && (
                    <div
                      className={classnames(
                        'flex h-[28px] w-[2px] items-center bg-black',
                        tabSpacerWidth
                      )}
                    >
                      <div className="bg-primary-dark h-[20px] w-full"></div>
                    </div>
                  )}
                  <TooltipProvider>
                    <Tooltip key={tabIndex}>
                      <TooltipTrigger>
                        <div
                          className={getTabClassNames(
                            numColumns,
                            tabs.length,
                            tabIndex,
                            tabIndex === activeTabIndex,
                            disabled
                          )}
                          style={getTabStyle(tabs.length)}
                          onClick={() => {
                            return disabled ? null : updateActiveTabIndex(tabIndex);
                          }}
                          data-cy={`${tab.name}-btn`}
                        >
                          <div
                            className={getTabIconClassNames(
                              tabs.length,
                              tabIndex === activeTabIndex
                            )}
                          >
                            {renderIcon(tab.iconName, {
                              className: classnames({
                                'text-lightblue-main': true,
                                'ohif-disabled': disabled,
                              }),
                              style: {
                                width: '22px',
                                height: '22px',
                              },
                            })}
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {getToolTipContent(tab.label, disabled)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {showDetach && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="ml-2 hidden lg:flex h-[28px] w-[28px] items-center justify-center text-lightblue-main hover:text-lightblue-dark hover:opacity-80"
                  title="Detach Report"
                  onClick={(e) => {
                    e.stopPropagation();
                    detachedReportWindow();
                  }}
                >
                  <TbUnlink size={15} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                sideOffset={1}
                className="bg-[#090c29] text-white rounded-md shadow-lg px-3 py-1.5 text-xs z-50"
              >
                Detach Report
                <TooltipPrimitive.Arrow className="h-3 w-4 fill-[#090c29]" />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  };

  const getOpenStateComponent = () => {
    return (
      <>
        <div className="bg-grey-dark flex h-[40px] select-none p-2">
          {getTabGridComponent()}
        </div>
        <Separator
          orientation="horizontal"
          className="bg-black"
          thickness="1px"
        />
      </>
    );
  };

  return (
    <div className={classnames(className, baseClasses, classesMap[openStatus][side], 'h-full w-full')}>
      {isOpen ? (
        <>
          {getOpenStateComponent()}
          {tabs.map((tab, tabIndex) => {
            if (tabIndex === activeTabIndex) {
              return <tab.content key={tabIndex} />;
            }
            return null;
          })}
        </>
      ) : (
        <React.Fragment>{getCloseStateComponent()}</React.Fragment>
      )}
    </div>
  );
};

SidePanel.propTypes = {
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  className: PropTypes.string,
  activeTabIndex: PropTypes.number,
  tabs: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        iconName: PropTypes.string.isRequired,
        iconLabel: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        content: PropTypes.func,
      })
    ),
  ]),
  onActiveTabIndexChange: PropTypes.func,
  expandedWidth: PropTypes.number,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
};

export { SidePanel };
